import React, { useCallback, useEffect, useState } from "react";
import Autosuggest, {
  SuggestionsFetchRequestedParams,
} from "react-autosuggest";
import debounce from "lodash.debounce";
import sortBy from "lodash.sortby";
import { connect } from "react-redux";
import uuid from "uuid/v4";
import { Spinner } from "@amzn/awsui-components-react-v3";
import autoSuggestTheme from "./auto-suggest.module.css";
import { RootState } from "../redux/rootReducer";
import {
  AutoSuggestItem,
  AutoSuggestResponse,
  AutoSuggestSection,
} from "./AutoSuggestResponse";
import EntityType from "./EntityType";
import { useHttpRequest, LoadingState } from "../http";
import { RequiredAxiosRequestConfig } from "../http/axiosModel";
import AmazonLogoSvg from "../svg/AmazonLogoSvg";
import AutoSuggestProps from "./AutoSuggestProps";
import { MetricsState } from "../logging";

const GET_SEARCH_RESULT_ACTION = "GET_SEARCH_RESULT";
const getSearchResultUrl = "/api/v1/search?q=";

const labelOrderMap = new Map([
  ["People", 0],
  ["Teams", 1],
  ["Organizations", 2],
]);

const orderSections = (
  sections: AutoSuggestSection[]
): AutoSuggestSection[] => {
  return sortBy(
    sections,
    (section: AutoSuggestSection) => labelOrderMap.get(section.label) || 0
  );
};

const getSectionResultsLimit = (sectionsCount: number): number => {
  if (sectionsCount <= 1) {
    return 12;
  }
  if (sectionsCount === 2) {
    return 6;
  }
  return 4;
};

const trimSuggestions = (
  sections: AutoSuggestSection[]
): AutoSuggestSection[] => {
  const limit = getSectionResultsLimit(sections.length);

  return sections.map((section) => {
    const results = section.results.slice(0, limit);
    return {
      ...section,
      results,
    };
  });
};

const AutoSuggest = (properties: AutoSuggestProps): JSX.Element => {
  const {
    placeholder,
    suggestionOnClick,
    onInputValueChange,
    suggestionTypes,
    searchQueryMinLength,
    metrics,
    isSquare,
  } = properties;
  const [id] = useState(uuid());
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<AutoSuggestSection[]>([]);

  const { data, sendRequest, reset, loadingState } = useHttpRequest<
    AutoSuggestResponse
  >(GET_SEARCH_RESULT_ACTION, metrics);

  useEffect(() => {
    if (data) {
      const transformedSuggestions = orderSections(trimSuggestions(data.data));
      setSuggestions(transformedSuggestions);
    }
  }, [data]);

  const getSuggestionValue = (suggestion: AutoSuggestItem): string =>
    suggestion.urlId;

  const getSectionSuggestions = (
    section: AutoSuggestSection
  ): AutoSuggestItem[] => section.results;

  const renderSectionTitle = (section: AutoSuggestSection): JSX.Element => (
    <div className={autoSuggestTheme["suggestion-section-name"]}>
      {section.label}
    </div>
  );

  const renderSuggestion = (suggestion: AutoSuggestItem): JSX.Element => {
    if (loadingState !== LoadingState.RESOLVED || suggestions.length === 0) {
      return (
        <div>
          {suggestion.title}
          {loadingState !== LoadingState.RESOLVED && <Spinner />}
        </div>
      );
    }
    return (
      <div className={autoSuggestTheme["suggestion-wrapper"]}>
        <div className={autoSuggestTheme["headshot-cropper"]}>
          {suggestion.type === EntityType.EMPLOYEE ? (
            <img
              src={suggestion.imageUrl}
              alt="headshot"
              className={autoSuggestTheme["headshot"]}
            />
          ) : (
            <AmazonLogoSvg />
          )}
        </div>
        <div className={autoSuggestTheme["suggestion-text-top-row"]}>
          {" "}
          {suggestion.title}{" "}
        </div>
        <div className={autoSuggestTheme["suggestion-text-bottom-row"]}>
          {" "}
          {suggestion.detail}{" "}
        </div>
      </div>
    );
  };

  const onValueChange = (
    _event: React.FormEvent,
    { newValue }: { newValue: string }
  ): void => {
    if (onInputValueChange) {
      onInputValueChange(newValue);
    }
    setInputValue(newValue);
  };

  const fetchSuggestions = (query: string): void => {
    const requestConfig: RequiredAxiosRequestConfig = {
      url: getSearchResultUrl + encodeURIComponent(query),
      method: "POST",
      data: { types: suggestionTypes },
    };
    sendRequest(requestConfig);
  };

  const debounceFetchSuggestions = useCallback(
    debounce((query: string) => fetchSuggestions(query), 500),
    []
  );

  const onSuggestionsFetchRequested = ({
    value,
  }: SuggestionsFetchRequestedParams): void => {
    if (value.length < searchQueryMinLength) {
      setSuggestions([]);
    } else {
      debounceFetchSuggestions(value);
    }
  };

  const onSuggestionsClearRequested = (): void => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (
    _: React.FormEvent,
    item: Autosuggest.SuggestionSelectedEventData<AutoSuggestItem>
  ): void => {
    if (loadingState === LoadingState.RESOLVED && suggestions.length !== 0) {
      suggestionOnClick(item.suggestion);
      setInputValue("");
      reset();
    }
  };

  const inputProperties = {
    placeholder,
    value: inputValue,
    onChange: onValueChange,
    type: "search",
  };

  const getConvertedSuggestions = (): any => {
    if (loadingState === LoadingState.LOADING) {
      return [
        {
          title: "Loading",
          urlId: inputValue,
        },
      ];
    }
    if (loadingState === LoadingState.RESOLVED && suggestions.length === 0) {
      return [
        {
          title: "We can’t find a match",
          urlId: inputValue,
        },
      ];
    }
    if (loadingState === LoadingState.REJECTED) {
      return [
        {
          title:
            "An unexpected error occurred. Retrying your search might resolve the issue.",
          urlId: inputValue,
        },
      ];
    }
    return suggestions;
  };

  return (
    <div
      className={
        isSquare ? autoSuggestTheme["square"] : autoSuggestTheme["round"]
      }
      data-testid="people-picker-input"
    >
      <Autosuggest
        theme={autoSuggestTheme}
        multiSection={
          loadingState === LoadingState.RESOLVED && suggestions.length !== 0
        }
        suggestions={getConvertedSuggestions()}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        inputProps={inputProperties}
        id={id}
      />
    </div>
  );
};

const mapStateToProperties = (state: RootState): { metrics: MetricsState } => ({
  metrics: state.metrics,
});

export default connect(mapStateToProperties)(AutoSuggest);
